package ai.jessie.web.customers

import ai.jessie.shared.customers.CustomerPreviewResponse
import ai.jessie.shared.customers.CustomerResponse
import ai.jessie.shared.customers.SaveCustomerRequest
import ai.jessie.web.members.deniesAccess
import ai.jessie.web.onboarding.OnboardingProblem
import ai.jessie.web.onboarding.OnboardingRequestFailure
import ai.jessie.web.onboarding.unavailableProblem
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.launch

data class CustomerUiState(
    val customers: List<CustomerResponse>? = null,
    val current: CustomerResponse? = null,
    val draft: CustomerDraft? = null,
    val preview: CustomerPreviewResponse? = null,
    val pending: SaveCustomerRequest? = null,
    val duplicateConfirmed: Boolean = false,
    val busy: Boolean = false,
    val error: OnboardingProblem? = null,
    val problems: Map<String, String> = emptyMap(),
    val notice: String? = null,
)

data class CustomerActions(
    val refresh: () -> Unit,
    val create: () -> Unit,
    val edit: () -> Unit,
    val archive: () -> Unit,
    val reactivate: () -> Unit,
    val change: ((CustomerDraft) -> CustomerDraft) -> Unit,
    val preview: () -> Unit,
    val confirmDistinct: (Boolean) -> Unit,
    val confirm: () -> Unit,
    val revise: () -> Unit,
    val cancel: () -> Unit,
    val close: () -> Unit,
)

/**
 * UC-012 management. Without [customerId] the state lists customers and creates one; with it the
 * state shows, edits, archives or reactivates that customer. Single-flight; a disposed page scope
 * cannot publish late success, error or finalisation. Nothing is saved before [confirm].
 */
class CustomerState(
    private val port: CustomerPort,
    private val scope: CoroutineScope,
    private val organisationId: String,
    private val customerId: String? = null,
) {
    var state by mutableStateOf(CustomerUiState())
        private set

    val actions =
        CustomerActions(
            refresh = ::refresh,
            create = { if (customerId == null) open { CustomerDraft() } },
            edit = { open { state.current?.takeIf { customerId != null }?.draft() } },
            // A8: the unchanged record with the new status goes straight to confirmation.
            archive = {
                if (state.draft == null) {
                    open { state.current?.takeIf { customerId != null }?.draft("ARCHIVED") }
                    preview()
                }
            },
            // A4: current details are reviewed and corrected before reactivation is confirmed.
            reactivate = { open { state.current?.takeIf { customerId != null }?.draft("ACTIVE") } },
            change = { update ->
                val draft = state.draft
                if (!state.busy && draft != null && state.preview == null)
                    state = state.copy(draft = update(draft))
            },
            preview = ::preview,
            confirmDistinct = {
                if (!state.busy && state.preview != null)
                    state = state.copy(duplicateConfirmed = it)
            },
            confirm = ::confirm,
            revise = {
                if (!state.busy)
                    state = state.copy(preview = null, pending = null, duplicateConfirmed = false)
            },
            // A12: cancelling sends nothing.
            cancel = {
                if (!state.busy)
                    state =
                        CustomerUiState(
                            customers = state.customers,
                            current = state.current,
                            notice = "Abgebrochen. Es wurde nichts gespeichert.",
                        )
            },
            close = {
                if (!state.busy) {
                    state = CustomerUiState()
                    refresh()
                }
            },
        )

    fun refresh() {
        if (state.draft != null) return
        request {
            if (customerId == null) {
                val customers = port.list(organisationId)
                currentCoroutineContext().ensureActive()
                state = state.copy(customers = customers)
            } else {
                val current = port.read(organisationId, customerId)
                currentCoroutineContext().ensureActive()
                state = state.copy(current = current)
            }
        }
    }

    private fun open(draft: () -> CustomerDraft?) {
        if (state.busy || state.draft != null) return
        draft()?.let { state = state.copy(draft = it, error = null, notice = null) }
    }

    private fun preview() {
        val draft = state.draft
        if (state.busy || draft == null || state.preview != null) return
        val invalid = buildMap {
            if (draft.type !in customerTypes) put("type", customerCorrection("type"))
            if (draft.displayName.isBlank()) put("displayName", customerCorrection("displayName"))
        }
        if (invalid.isNotEmpty()) {
            state = state.copy(problems = invalid, error = customerValidationProblem(invalid))
            return
        }
        val proposal = draft.request(state.current?.takeIf { customerId != null }?.updatedAt)
        request {
            val preview = port.preview(organisationId, customerId, proposal)
            currentCoroutineContext().ensureActive()
            state = state.copy(preview = preview, pending = proposal, duplicateConfirmed = false)
        }
    }

    private fun confirm() {
        val proposal = state.pending
        val duplicates = state.preview?.duplicates
        // A7: possible duplicates need the member's explicit confirmation first.
        if (proposal == null || duplicates == null) return
        if (duplicates.isNotEmpty() && !state.duplicateConfirmed) return
        request {
            val saved =
                port.save(
                    organisationId,
                    customerId,
                    proposal.copy(confirmed = true, duplicateConfirmed = state.duplicateConfirmed),
                )
            currentCoroutineContext().ensureActive()
            state = CustomerUiState(current = saved, busy = true, notice = savedNotice(saved))
        }
    }

    private fun request(block: suspend () -> Unit) {
        if (state.busy) return
        state = state.copy(busy = true, error = null, problems = emptyMap(), notice = null)
        scope.launch {
            try {
                block()
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: CustomerRequestFailure) {
                currentCoroutineContext().ensureActive()
                rejected(failure)
            } catch (_: Exception) {
                currentCoroutineContext().ensureActive()
                // Only an unanswered save leaves the stored state unknown; reads changed nothing.
                val error =
                    if (state.pending != null) uncertainCustomerProblem() else unavailableProblem()
                state = state.copy(preview = null, pending = null, error = error)
            } finally {
                currentCoroutineContext().ensureActive()
                state = state.copy(busy = false)
            }
        }
    }

    private fun rejected(failure: CustomerRequestFailure) {
        val detail = failure.detail
        val common = OnboardingRequestFailure(failure.status, detail.code, detail.fields)
        val error = failure.customerProblem()
        state =
            when {
                // A11 and a stale record: nothing previously loaded stays on the page.
                common.deniesAccess() || detail.code == "STALE_CUSTOMER" ->
                    CustomerUiState(busy = true, error = error)
                detail.code == "INVALID_CUSTOMER" -> {
                    val problems = detail.fields.associateWith(::customerCorrection)
                    state.copy(
                        preview = null,
                        pending = null,
                        problems = problems,
                        error = customerValidationProblem(problems),
                    )
                }
                detail.code == "DUPLICATE_CONFIRMATION_REQUIRED" ->
                    state.copy(
                        preview = CustomerPreviewResponse(detail.duplicates),
                        duplicateConfirmed = false,
                        error = error,
                    )
                // A13: the review stays open so that the member can confirm again.
                failure.status >= HTTP_SERVER_ERROR -> state.copy(error = error)
                else -> state.copy(preview = null, pending = null, error = error)
            }
    }
}

private fun savedNotice(saved: CustomerResponse) =
    if (saved.status == "ARCHIVED") "Kunde archiviert. Bestehende Verknüpfungen bleiben erhalten."
    else "Kunde gespeichert. Er steht für zulässige Projekte zur Verfügung."

internal val customerTypes = mapOf("COMPANY" to "Firma", "INDIVIDUAL" to "Privatperson")
private const val HTTP_SERVER_ERROR = 500
